import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ProvideConfig } from '@local/app-config';
import { ErrorBoundary } from '@local/error-logging';
import { MetricsWrapper } from '@local/metrics';
import { ErrorScreen } from '@local/svgs/dist/pageState/ErrorScreen';
import { WDSThemeProvider } from '@local/web-design-system-2/dist/theme/WDSThemeProvider';
import { SnackbarProvider } from 'notistack';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { IntlProvider, useIntl } from 'react-intl';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import { TssCacheProvider } from 'tss-react';

import App from './App';
import { i18n } from './main.i18n';
import { store } from './store/store';

const muiCache = createCache({
    key: 'mui',
    prepend: true,
});

const tssCache = createCache({
    key: 'tss',
});

const ErrorFallback = () => {
    const intl = useIntl();
    return (
        <ErrorScreen
            msg={intl.formatMessage(i18n.message)}
            details={intl.formatMessage(i18n.details)}
        />
    );
};

// We only send data to sentry and datadog for release builds (NODE_ENV production), not dev builds.
const recordMetrics = process.env.NODE_ENV === 'production';

// TODO document how to create
// Taken from https://sentry.io/settings/seequent-ltd/projects/blockmodelservice-ui/keys/
const sentryDsn = '';

// TODO document how to create
// Taken from https://app.datadoghq.com/rum/application/7264c6d8-a1fc-42ed-ba1f-b6ad0979482c
const datadogConfig = { appId: '', clientToken: '' };

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
        <IntlProvider locale="en">
            <ProvideConfig>
                <CacheProvider value={muiCache}>
                    <TssCacheProvider value={tssCache}>
                        <WDSThemeProvider themeMode="light">
                            <ErrorBoundary
                                errorComponent={<ErrorFallback />}
                                sentryDSN={recordMetrics ? sentryDsn : undefined}
                            >
                                <MetricsWrapper
                                    datadogConfig={recordMetrics ? datadogConfig : undefined}
                                >
                                    <Provider store={store}>
                                        <SnackbarProvider maxSnack={3}>
                                            <BrowserRouter>
                                                <App />
                                            </BrowserRouter>
                                        </SnackbarProvider>
                                    </Provider>
                                </MetricsWrapper>
                            </ErrorBoundary>
                        </WDSThemeProvider>
                    </TssCacheProvider>
                </CacheProvider>
            </ProvideConfig>
        </IntlProvider>
    </StrictMode>,
);
